import { Options } from "@angular-slider/ngx-slider";
import { NgTemplateOutlet } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SharedModule } from "@app/shared/shared.module";
import { AuctionStatus, SessionKey } from "@shared/clickar-constants";
import { LotFullDTO, Purchase } from "@shared/model/lotDTO";
import { UserDTO } from "@shared/model/user.model";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { CodeTable, CT } from "@shared/services/code-table/code-table.model";
import { CodeTableService } from "@shared/services/code-table/code-table.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UsersService } from "@shared/services/users/users.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { BsDatepickerConfig, BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { fromEvent, Subject } from "rxjs";
import { auditTime, debounceTime, take } from "rxjs/operators";

type ConfigKey = "auctionDetails" | "purchase";
/** Used to store filter status in SessionStorage */
interface FilterStatus {
	/** Previous filter value */
	value: string | [number, number] | [string, string] /* ISO 8601 date strings */ | [Date, Date] /* Dates after conversion from strings */;
	/** Latest number/date range of data */
	range?: [number, number] | [string, string] /* ISO 8601 date strings */ | [Date, Date] /* Dates after conversion from strings */;
}
interface FilterConfig<T> {
	filter: {
		/** Filters the corresponding values
		 * @param toBeFiltered - the items to be filtered
		 * @param value - the values to be compared with
		*/
		singleLevel: <T >(
			toBeFiltered: T[],
			value: string | [number, number] | [Date, Date] // number, rangeslider, from-to dates
		) => T[];
		multiLevel?: (
			toBeFiltered: LotFullDTO[],
			value: string | [number, number]
		) => LotFullDTO[];
	};

	/** Resets field value */
	reset: (config: FilterConfig<T>) => void;

	/** Current value */
	value?: string;

	/** Extreme values for from-to date filters */
	dateRange?: [Date, Date]

	/** Current value for from-to date filters. */
	dateValues?: [Date, Date] | [null, null]

	/** Current value for range sliders */
	rangeValue?: [number, number];

	/** Filter initialization method */
	init?: (config: FilterConfig<T>) => void;

	/** The possible values in selectbox */
	values?: [number, number] | string[] | CodeTable[];

	/** ngx-slider configuration */
	sliderConfig?: Options;

	/** If present and true, the field must be hidden (missing data, requested by market, etc) */
	hideThis?: boolean;
};

type FilterConfigurationSet<T> = {
	[key: string]: FilterConfig<T>;
};

interface SessionSavedStatus { [auctionIDorPurchase: string]: { [key: string]: FilterStatus; }; };

@Component({
	selector: "app-filters",
	imports: [SharedModule, NgTemplateOutlet, BsDatepickerModule],
	templateUrl: "./filters.component.html",
	styleUrl: "./filters.component.scss",
})

export class FiltersComponent implements OnInit, OnDestroy {
	private codeTableService = inject(CodeTableService);
	private userService = inject(UsersService);
	private utils = inject(ClickarUtilsService);
	private storageService = inject(StorageManagerService);

	protected bsConfig: Partial<BsDatepickerConfig> = {
		adaptivePosition: true,
		containerClass: "theme-red",
		dateInputFormat: "DD/MM/YYYY"
	};

	/**
	 * Any slider field can be shown or not on this basis:
	 *
	 * | NO Single lot values | NO Multiple lot values | 1 Single lot value | 1 Multiple lot value | Behavior |
	 * | :---: | :---: | :---: | :---: | :---: |
	 * | · | · | · | · | Expected normal behavior |
	 * | · | · | · | ✓ | Normal behavior if both types of lots are present, hidden if there are only multiple lots |
	 * | · | · | ✓ | · | Normal behavior if both types of lots are present, hidden if there are only single lots |
	 * | · | ✓ | · | · | Normal behavior if only single lots are present, hidden if only multiples (, ignored for multiples if both, but the null check is included in filter, so no further check is needed)|
	 * | ✓ | · | · | · | Normal behavior if only multiple lots are present, hidden if only singles (, ignored for singles if both, but the null check is included in filter, so no further check is needed)|
	 * | · | · | ✓ | ✓ | Hidden field |
	 * | · | ✓ | ✓ | · | Hidden field |
	 * | ✓ | · | · | ✓ | Hidden field |
	 * | ✓ | ✓ | · | · | Hidden field |
	 * | · | ✓ | · | ✓ | (Impossible) |
	 * | · | ✓ | ✓ | ✓ | (Impossible) |
	 * | ✓ | · | ✓ | · | (Impossible) |
	 * | ✓ | · | ✓ | ✓ | (Impossible) |
	 * | ✓ | ✓ | · | ✓ | (Impossible) |
	 * | ✓ | ✓ | ✓ | · | (Impossible) |
	 * | ✓ | ✓ | ✓ | ✓ | (Impossible) |
	*
	 */
	private readonly shouldFilterBeHidden = (
		singleEdges: [number, number],
		multipleEdges: [number, number] = [Infinity, -Infinity]
	): boolean =>
		// Filter is useless for single: there are no items or there is no valid value or there is just one value among the items
		(!this.singleLevelItems.length || [Infinity, singleEdges[1]].includes(singleEdges[0]))
		&&
		// Filter is useless for multiple: there are no items or there is no valid value or there is just one value among the items
		(!this.multiLevelItems.length || [Infinity, multipleEdges[1]].includes(multipleEdges[0]));

	/* {
		const noValidValuesSingle = singleEdges[0] === Infinity,
			noValidValuesMultiple = multipleEdges[0] === Infinity,
			singleValueSingle = singleEdges[0] === singleEdges[1],
			singleValueMultiple = multipleEdges[0] === multipleEdges[1];
		return ((noValidValuesSingle || singleValueSingle) && (noValidValuesMultiple || singleValueMultiple)) ||
			((noValidValuesSingle || singleValueSingle) && !this.multiLevelItems.length) ||
			((noValidValuesMultiple || singleValueMultiple) && !this.singleLevelItems.length);
	} */

	constructor() {
		fromEvent<BeforeUnloadEvent>(window, 'beforeunload')
			.pipe(takeUntilDestroyed())
			.subscribe(() => {
				this.ngOnDestroy();
			});
	}

	@Input() configuration: ConfigKey;
	/** Needed when damage filter is expected to be shown:
	 * some markets want it hidden */
	@Input() market = "";

	/** Auction-details filter setting is saved per-auction.
	 * This input must contain the AuctionID */
	@Input() idAuction: number;

	/* For filtering by lot status */
	/* private _auction:AuctionDTO;
	@Input() set auction(auction: AuctionDTO) {
		this._auction=auction;
		} */

	singleLevelItems: VehicleDTO[] | Purchase[] = [];

	/** Emits updates of input setter, in order to avoid a storm of recalculations due to mqtt updates */
	private refreshSubject = new Subject<void>();

	@Input() set singleLevel(value: VehicleDTO[] | Purchase[]) {
		// console.info("set single", value);
		this.singleLevelItems = value;
		// console.info("primo next")

		this.refreshSubject.next();
	}

	multiLevelItems: LotFullDTO[] = [];

	@Input() set multiLevel(value: LotFullDTO[]) {
		// console.info("set multi", value);
		this.multiLevelItems = value;
		this.refreshSubject.next();
	}

	private refreshFilters = () => {
		if (this.filterConfiguration) {
			this.initFilters();
			this.applyFilters(false);
			// this.filterSubject.next(); // NOTE: not using this, cause refreshFilters call is already delayed by auditTime, and there is no need to further delay it with filterSubject
			// console.info("refresh")
		}
	}

	@Output() singleLevelChange = new EventEmitter<{
		/** Filtered data */
		data: VehicleDTO[] | Purchase[],
		/** Tells whether che change has been triggered by filters change or not (e.g. change of input data) */
		filtersHaveChanged: boolean
	}>();
	@Output() multiLevelChange = new EventEmitter<{
		/** Filtered data */
		data: LotFullDTO[],
		/** Tells whether che change has been triggered by filters change or not (e.g. change of input data) */
		filtersHaveChanged: boolean
	}>();

	// private getMinMax = ()=>{}

	private getOrderOfMagnitude(x: number) { return Math.pow(10, Math.floor(Math.log10(Math.abs(x)))); }

	/** Returns the initialization edge values and step for the slider, rounded to the closest tenth of higher value's order of magnitude
	 * (e.g. 1200 to 36000 rounds to 1000 to 36000 with 1000-wide steps,
	 * 120 to 36000 rounds to 0 to 36000 with 1000-wide steps,
	 * 120 to 3600 rounds to 100 to 3600 with 100-wide steps)
	 * Lower edge is floored, higher is ceiled
	 */
	private getSliderEdgesAndStep(valueEdges: [number, number]): { edges: [number, number], step: number } {
		const oom = valueEdges[1] ? this.getOrderOfMagnitude(valueEdges[1]) / 10 : 1; // In case valueEdges[1]===0, we would divide 0/0 => NaN
		return { edges: [Math.floor(valueEdges[0] / oom) * oom, Math.ceil(valueEdges[1] / oom) * oom], step: oom };
	}

	private textConfiguration = (attr: keyof (VehicleDTO | Purchase)) => ({
		filter: {
			singleLevel: (values: VehicleDTO[] | Purchase[], value: string) => !(value = value?.toLowerCase()) ? // checking the existance and converting to lowercase at the same time
				values :
				values.filter(v => (v[attr] as string)?.toLowerCase().includes(value)),
			multiLevel: (values: LotFullDTO[], value: string) => !(value = value?.toLowerCase()) ? // checking the existance and converting to lowercase at the same time
				values :
				values.filter(v => v.vehiclesDTO.some(v => (v[attr] as string)?.toLowerCase().includes(value)))
		},
		reset: (config: FilterConfig<VehicleDTO | Purchase>) => { (this.filterStatuses[attr] ??= {} as FilterStatus).value = config.value = ""; },
		value: "",
		init: (config: FilterConfig<VehicleDTO | Purchase>) => {
			if (this.filterStatuses[attr]?.value) {
				config.value = this.filterStatuses[attr].value as string;
			}
		}
	});

	private readonly allConfigurations: { [key in ConfigKey]: FilterConfigurationSet<VehicleDTO> | FilterConfigurationSet<Purchase> } = (() => {
		const brand = {
			filter: {
				singleLevel: (values: VehicleDTO[] | Purchase[], value: string) => !value ? values : values.filter(v => v.brand === value),
				multiLevel: (values: LotFullDTO[], value: string) => !value ? values : values.filter(v => v.vehiclesDTO.some(v => v.brand === value))
			},
			reset: (config: FilterConfig<VehicleDTO | Purchase>) => { (this.filterStatuses.brand ??= {} as FilterStatus).value = config.value = ""; },
			value: "",
			values: [],
			init: (config: FilterConfig<VehicleDTO | Purchase>) => {
				const allOptions = new Set<string>();
				for (const v of this.singleLevelItems as VehicleDTO[]) {
					if (v.brand) {
						allOptions.add(v.brand);
					}
				}
				for (const l of this.multiLevelItems) {
					for (const v of l.vehiclesDTO) {
						if (v.brand) {
							allOptions.add(v.brand);
						}
					}
				}
				config.values = Array.from(allOptions).sort();
				// console.info("values", config.values);

				if (this.filterStatuses.brand?.value) {
					config.value = this.filterStatuses.brand.value as string;
				}
			}
		},
			model = this.textConfiguration("model"),
			licensePlate = this.textConfiguration("licensePlate"),
			frame = this.textConfiguration("frame");

		return {
			auctionDetails:
				{
					status: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: string) => !value ? values : values.filter(v => v.calculatedStatus === value || (v.calculatedStatus === "EXTRA" && value === AuctionStatus.RUNNING)),
							multiLevel: (values: LotFullDTO[], value: string) => !value ? values : values.filter(v => v.lotDTO.calculatedStatus === value || (v.lotDTO.calculatedStatus === "EXTRA" && value === AuctionStatus.RUNNING)),
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.status ??= {} as FilterStatus).value = config.value = ""; },
						value: "",
						values: [],
						init: (config: FilterConfig<VehicleDTO>) => {
							if (!config.values!.length) {
								this.codeTableService
									.getCTElementsUsingGET(
										CT[CT.LOT_SEARCH_STATUS],
										this.user.idMarket
									)
									.subscribe(value => {
										config.values = value;
									});
							}
							if (this.filterStatuses.status?.value) {
								config.value = this.filterStatuses.status.value as string;
							}
						}
					},
					brand,
					model,
					area: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: string) => !value ? values : values.filter(v => v.locateArea === value),
							multiLevel: (values: LotFullDTO[], value: string) => !value ? values : values.filter(v => v.vehiclesDTO.some(v => v.locateArea === value))
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.area ??= {} as FilterStatus).value = config.value = ""; },
						value: "",
						values: [],
						init: (config: FilterConfig<VehicleDTO>) => {
							if (!config.values!.length) {
								this.codeTableService
									.getCTElementsUsingGET(
										CT[CT.COUNTRY_AREA],
										this.vehicleMarket
									)
									.subscribe(value => {
										config.values = value;
									});
							}
							if (this.filterStatuses.area?.value) {
								config.value = this.filterStatuses.area.value as string;
							}
						}
					},
					year: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: [number, number]) => value[0] === this.filterConfiguration.year.sliderConfig!.floor && value[1] === this.filterConfiguration.year.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => !isNaN(v.yearMatriculation) && v.yearMatriculation >= value[0] && v.yearMatriculation <= value[1]),
							multiLevel: (values: LotFullDTO[], value: [number, number]) => value[0] === this.filterConfiguration.year.sliderConfig!.floor && value[1] === this.filterConfiguration.year.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => v.vehiclesDTO.some(v => !isNaN(v.yearMatriculation) && v.yearMatriculation >= value[0] && v.yearMatriculation <= value[1]))
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.year ??= {} as FilterStatus).value = config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!]; },
						sliderConfig: {},
						init: (config: FilterConfig<VehicleDTO>) => {
							const singleEdges = [Infinity, -Infinity] as [number, number],
								multipleEdges = [Infinity, -Infinity] as [number, number];
							for (const v of this.singleLevelItems as VehicleDTO[]) {
								if (!isNaN(v.yearMatriculation)) {
									if (v.yearMatriculation === -1) {
										v.yearMatriculation = null;
									} else {
										if (singleEdges[0] > v.yearMatriculation) {
											singleEdges[0] = v.yearMatriculation;
										}
										if (singleEdges[1] < v.yearMatriculation) {
											singleEdges[1] = v.yearMatriculation;
										}
									}
								}
							}
							for (const l of this.multiLevelItems) {
								for (const v of l.vehiclesDTO) {
									if (!isNaN(v.yearMatriculation)) {
										if (v.yearMatriculation === -1) {
											v.yearMatriculation = null;
										} else {
											if (multipleEdges[0] > v.yearMatriculation) {
												multipleEdges[0] = v.yearMatriculation;
											}
											if (multipleEdges[1] < v.yearMatriculation) {
												multipleEdges[1] = v.yearMatriculation;
											}
										}
									}
								}
							}

							if (!(config.hideThis = this.shouldFilterBeHidden(singleEdges, multipleEdges))) {
								const edges = [Math.min(singleEdges[0], multipleEdges[0]), Math.max(singleEdges[1], multipleEdges[1])];
								/* // setting a range in case they are equal
								if (edges[0] === edges[1]) {
									edges[0]--; edges[1]++;
								} */

								config.sliderConfig = { floor: edges[0], ceil: edges[1], step: 1 };
								config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!];

								/* If user previously selected values, they differed from previous edges.
								If they are equal, we keep them to the newly calculated edges.
								If they aren't
									1 - if they are still within the new range, we reset them to the previous value (e.g. I selected 2018 to 2020 and now the range is 2015 - 2025, I keep 2018-2020)
									2 - if not, previous range is cropped to the new limits (e.g. I selected 2016 to 2024 in a 2015-2025 range, but now the oldest car is from 2019, so the new value is 2019-2025)
								*/
								if (this.filterStatuses.year) {
									if (this.filterStatuses.year.value) {
										const previousRange = this.filterStatuses.year.range as [number, number],
											previousValues = this.filterStatuses.year.value as [number, number];
										if (previousValues[0] !== previousRange[0] && previousValues[0] > edges[0] && previousValues[0] < edges[1]) {
											config.rangeValue[0] = previousValues[0];
										}
										else {
											previousValues[0] = edges[0];
										}
										if (previousValues[1] !== previousRange[1] && previousValues[1] > edges[0] && previousValues[1] < edges[1]) {
											config.rangeValue[1] = previousValues[1];
										}
										else {
											previousValues[1] = edges[1];
										}
										previousRange[0] = edges[0];
										previousRange[1] = edges[1];
									}
								}
								else {
									this.filterStatuses.year = { value: config.rangeValue, range: config.rangeValue };
								}
							}
						}
					},
					km: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: [number, number]) => value[0] === this.filterConfiguration.km.sliderConfig!.floor && value[1] === this.filterConfiguration.km.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => !isNaN(v.kilometers) && v.kilometers >= value[0] && v.kilometers <= value[1]),
							multiLevel: (values: LotFullDTO[], value: [number, number]) => value[0] === this.filterConfiguration.km.sliderConfig!.floor && value[1] === this.filterConfiguration.km.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => v.vehiclesDTO.some(v => !isNaN(v.kilometers) && v.kilometers >= value[0] && v.kilometers <= value[1]))
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.km ??= {} as FilterStatus).value = config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!]; },
						sliderConfig: {},
						init: (config: FilterConfig<VehicleDTO>) => {
							const singleEdges = [Infinity, -Infinity] as [number, number],
								multipleEdges = [Infinity, -Infinity] as [number, number];
							for (const v of this.singleLevelItems as VehicleDTO[]) {
								if (!isNaN(v.kilometers)) {
									if (v.kilometers === -1) {
										v.kilometers = null;
									} else {
										if (singleEdges[0] > v.kilometers) {
											singleEdges[0] = v.kilometers;
										}
										if (singleEdges[1] < v.kilometers) {
											singleEdges[1] = v.kilometers;
										}
									}
								}
							}
							for (const l of this.multiLevelItems) {
								for (const v of l.vehiclesDTO) {
									if (!isNaN(v.kilometers)) {
										if (v.kilometers === -1) {
											v.kilometers = null;
										} else {
											if (multipleEdges[0] > v.kilometers) {
												multipleEdges[0] = v.kilometers;
											}
											if (multipleEdges[1] < v.kilometers) {
												multipleEdges[1] = v.kilometers;
											}
										}
									}
								}
							}

							if (!(config.hideThis = this.shouldFilterBeHidden(singleEdges, multipleEdges))) {
								const edgesAndStep = this.getSliderEdgesAndStep([Math.min(singleEdges[0], multipleEdges[0]), Math.max(singleEdges[1], multipleEdges[1])]);
								// setting a range in case they are equal

								/* if (edgesAndStep.edges[0] === edgesAndStep.edges[1]) {
									// setting an interval around the only available value, 1/10th of order of magnitude (min 1)
									const span = Math.max(this.getOrderOfMagnitude(edgesAndStep.edges[0]) / 10, 1);
									edgesAndStep.edges[0] -= span; edgesAndStep.edges[1] += span;
									edgesAndStep.step = span;
								} */
								config.sliderConfig = { floor: edgesAndStep.edges[0], ceil: edgesAndStep.edges[1], step: edgesAndStep.step };
								config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!];

								/* If user previously selected values, they differed from previous edges.
								If they are equal, we keep them to the newly calculated edges.
								If they aren't
									1 - if they are still within the new range, we reset them to the previous value (e.g. I selected 30000 to 70000 and now the range is 15000 - 100000, I keep 30000-70000)
									2 - if not, previous range is cropped to the new limits (e.g. I selected 15000 to 40000 in a 10000-50000 range, but now the car with maximum km is 30000, so the new value is 15000-30000)
								*/
								if (this.filterStatuses.km) {
									if (this.filterStatuses.km.value) {
										const previousRange = this.filterStatuses.km.range as [number, number],
											previousValues = this.filterStatuses.km.value as [number, number];
										if (previousValues[0] !== previousRange[0] && previousValues[0] > edgesAndStep.edges[0] && previousValues[0] < edgesAndStep.edges[1]) {
											config.rangeValue[0] = previousValues[0];
										}
										else {
											previousValues[0] = edgesAndStep.edges[0];
										}
										if (previousValues[1] !== previousRange[1] && previousValues[1] > edgesAndStep.edges[0] && previousValues[1] < edgesAndStep.edges[1]) {
											config.rangeValue[1] = previousValues[1];
										}
										else {
											previousValues[1] = edgesAndStep.edges[1];
										}
										previousRange[0] = edgesAndStep.edges[0];
										previousRange[1] = edgesAndStep.edges[1];
									}
								}
								else {
									this.filterStatuses.km = { value: config.rangeValue, range: config.rangeValue };
								}
							}
						}
					},
					budget: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: [number, number]) => value[0] === this.filterConfiguration.budget.sliderConfig!.floor && value[1] === this.filterConfiguration.budget.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => !isNaN(v.actualPrice) && v.actualPrice >= value[0] && v.actualPrice <= value[1]),
							multiLevel: (values: LotFullDTO[], value: [number, number]) => value[0] === this.filterConfiguration.budget.sliderConfig!.floor && value[1] === this.filterConfiguration.budget.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(l => !isNaN(l.lotDTO.actualPrice) && l.lotDTO.actualPrice >= value[0] && l.lotDTO.actualPrice <= value[1])
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.budget ??= {} as FilterStatus).value = config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!]; },
						sliderConfig: {},
						init: (config: FilterConfig<VehicleDTO>) => {
							const singleEdges = [Infinity, -Infinity] as [number, number],
								multipleEdges = [Infinity, -Infinity] as [number, number];
							for (const v of this.singleLevelItems as VehicleDTO[]) {
								if (!isNaN(v.actualPrice)) {
									if (singleEdges[0] > v.actualPrice) {
										singleEdges[0] = v.actualPrice;
									}
									if (singleEdges[1] < v.actualPrice) {
										singleEdges[1] = v.actualPrice;
									}
								}
							}
							for (const l of this.multiLevelItems) {
								if (!isNaN(l.lotDTO.actualPrice)) {
									if (multipleEdges[0] > l.lotDTO.actualPrice) {
										multipleEdges[0] = l.lotDTO.actualPrice;
									}
									if (multipleEdges[1] < l.lotDTO.actualPrice) {
										multipleEdges[1] = l.lotDTO.actualPrice;
									}
								}
							}
							/*
							NOTE: not considering the "hideability" of the field cause price will change,
							and the field (which will almost never be hidden, since there is always a price, but unlikely identical)
							may pop-in/out.
							Other filter attributes will never change, hence the field will always be hidden or shown.
							if (!(config.hideThis = this.shouldFilterBeHidden(singleEdges, multipleEdges))) {
							*/
							const edgesAndStep = this.getSliderEdgesAndStep([Math.min(singleEdges[0], multipleEdges[0]), Math.max(singleEdges[1], multipleEdges[1])]);
							// setting a range in the unlikely case they are equal
							if (edgesAndStep.edges[0] === edgesAndStep.edges[1]) {
								// setting an interval around the only available value, 1/10th of order of magnitude (min 1)
								const span = Math.max(this.getOrderOfMagnitude(edgesAndStep.edges[0]) / 10, 1);
								edgesAndStep.edges[0] -= span; edgesAndStep.edges[1] += span;
								edgesAndStep.step = span;
							}
							config.sliderConfig = { floor: edgesAndStep.edges[0], ceil: edgesAndStep.edges[1], step: edgesAndStep.step };
							config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!];

							/* If user previously selected values, they differed from previous edges.
							If they are equal, we keep them to the newly calculated edges.
							If they aren't
								1 - if they are still within the new range, we reset them to the previous value (e.g. I selected 1000 to 2000 and now the range is 0 - 3000, I keep 1000-2000)
								2 - if not, previous range is cropped to the new limits (e.g. I selected 2000 to 5000 in a 0-7500 range, but now the price range is 8000-13000, so the new value is 8000-13000)
							*/
							if (this.filterStatuses.budget) {
								if (this.filterStatuses.budget.value) {
									const previousRange = this.filterStatuses.budget.range as [number, number],
										previousValues = this.filterStatuses.budget.value as [number, number];
									if (previousValues[0] !== previousRange[0] && previousValues[0] > edgesAndStep.edges[0] && previousValues[0] < edgesAndStep.edges[1]) {
										config.rangeValue[0] = previousValues[0];
									}
									else {
										previousValues[0] = edgesAndStep.edges[0];
									}
									if (previousValues[1] !== previousRange[1] && previousValues[1] > edgesAndStep.edges[0] && previousValues[1] < edgesAndStep.edges[1]) {
										config.rangeValue[1] = previousValues[1];
									}
									else {
										previousValues[1] = edgesAndStep.edges[1];
									}
									previousRange[0] = edgesAndStep.edges[0];
									previousRange[1] = edgesAndStep.edges[1];
								}
							}
							else {
								this.filterStatuses.budget = { value: config.rangeValue, range: config.rangeValue };
							}
							/* } */
						}
					},
					damage: {
						filter: {
							singleLevel: (values: VehicleDTO[], value: [number, number]) => value[0] === this.filterConfiguration.damage.sliderConfig!.floor && value[1] === this.filterConfiguration.damage.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => !isNaN(v.damaged) && v.damaged >= value[0] && v.damaged <= value[1]),
							multiLevel: (values: LotFullDTO[], value: [number, number]) => value[0] === this.filterConfiguration.damage.sliderConfig!.floor && value[1] === this.filterConfiguration.damage.sliderConfig!.ceil ? // Checking whether the sliders are set to the edges: in that case, there is no need to filter
								values :
								values.filter(v => v.vehiclesDTO.some(v => !isNaN(v.damaged) && v.damaged >= value[0] && v.damaged <= value[1]))
						},
						reset: (config: FilterConfig<VehicleDTO>) => { (this.filterStatuses.damage ??= {} as FilterStatus).value = config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!]; },
						sliderConfig: {},
						init: (config: FilterConfig<VehicleDTO>) => {
							if (!["PT", "PL", "LU"].includes(this.market)) {
								const singleEdges = [Infinity, -Infinity] as [number, number],
									multipleEdges = [Infinity, -Infinity] as [number, number];
								for (const v of this.singleLevelItems as VehicleDTO[]) {
									if (!isNaN(v.damaged!)) {
										if (v.damaged === -1) {
											v.damaged = null;
										} else {
											if (singleEdges[0] > v.damaged!) {
												singleEdges[0] = v.damaged!;
											}
											if (singleEdges[1] < v.damaged!) {
												singleEdges[1] = v.damaged!;
											}
										}
									}
								}
								for (const l of this.multiLevelItems) {
									for (const v of l.vehiclesDTO) {
										if (!isNaN(v.damaged!)) {
											if (v.damaged === -1) {
												v.damaged = null;
											} else {
												if (multipleEdges[0] > v.damaged!) {
													multipleEdges[0] = v.damaged!;
												}
												if (multipleEdges[1] < v.damaged!) {
													multipleEdges[1] = v.damaged!;
												}
											}
										}
									}
								}
								if (!(config.hideThis = this.shouldFilterBeHidden(singleEdges, multipleEdges))) {
									const edgesAndStep = this.getSliderEdgesAndStep([Math.min(singleEdges[0], multipleEdges[0]), Math.max(singleEdges[1], multipleEdges[1])]);
									// setting a range in case they are equal
									/* if (edgesAndStep.edges[0] === edgesAndStep.edges[1]) {
										// setting an interval around the only available value, 1/10th of order of magnitude (min 1)
										const span = Math.max(this.getOrderOfMagnitude(edgesAndStep.edges[0]) / 10, 1);
										edgesAndStep.edges[0] -= span; edgesAndStep.edges[1] += span;
										edgesAndStep.step = span;
									} */

									config.sliderConfig = { floor: edgesAndStep.edges[0], ceil: edgesAndStep.edges[1], step: edgesAndStep.step };
									config.rangeValue = [config.sliderConfig!.floor!, config.sliderConfig!.ceil!];

									/* If user previously selected values, they differed from previous edges.
									If they are equal, we keep them to the newly calculated edges.
									If they aren't
										1 - if they are still within the new range, we reset them to the previous value (e.g. I selected 1000 to 2000 and now the range is 0 - 3000, I keep 1000-2000)
										2 - if not, previous range is cropped to the new limits (e.g. I selected 200 to 500 in a 0-750 range, but now the damage range is 300-400, so the new value is 300-400)
									*/
									if (this.filterStatuses.damage) {
										if (this.filterStatuses.damage.value) {
											const previousRange = this.filterStatuses.damage.range as [number, number],
												previousValues = this.filterStatuses.damage.value as [number, number];
											if (previousValues[0] !== previousRange[0] && previousValues[0] > edgesAndStep.edges[0] && previousValues[0] < edgesAndStep.edges[1]) {
												config.rangeValue[0] = previousValues[0];
											}
											else {
												previousValues[0] = edgesAndStep.edges[0];
											}
											if (previousValues[1] !== previousRange[1] && previousValues[1] > edgesAndStep.edges[0] && previousValues[1] < edgesAndStep.edges[1]) {
												config.rangeValue[1] = previousValues[1];
											}
											else {
												previousValues[1] = edgesAndStep.edges[1];
											}
											previousRange[0] = edgesAndStep.edges[0];
											previousRange[1] = edgesAndStep.edges[1];
										}
									}
									else {
										this.filterStatuses.damage = { value: config.rangeValue, range: config.rangeValue };
									}
								}
							} else {
								config.hideThis = true;
							}
						}
					},
					licensePlate,
					frame
				} as FilterConfigurationSet<VehicleDTO>,
			purchase: {
				templateName: {
					filter: {
						singleLevel: (values: Purchase[], value: string) => !value ? values : values.filter(v => v.templateName === value)
					},
					reset: (config: FilterConfig<Purchase>) => { (this.filterStatuses.templateName ??= {} as FilterStatus).value = config.value = ""; },
					value: "",
					values: [],
					init: (config: FilterConfig<Purchase>) => {
						const allOptions = new Set<string>();
						for (const v of this.singleLevelItems as Purchase[]) {
							if (v.templateName) {
								allOptions.add(v.templateName);
							}
						}
						config.values = Array.from(allOptions).sort();
						// console.info("values", config.values);

						if (this.filterStatuses.purchase?.value) {
							config.value = this.filterStatuses.purchase.value as string;
						}
					}
				},
				brand,
				model,
				licensePlate,
				frame,
				channel: {
					filter: {
						singleLevel: (values: Purchase[], value: string) => !value ? values : values.filter(v => v.channel === value)
					},
					reset: (config: FilterConfig<Purchase>) => { (this.filterStatuses.channel ??= {} as FilterStatus).value = config.value = ""; },
					value: "",
					values: [],
					init: (config: FilterConfig<Purchase>) => {
						if (!config.values.length) {
							this.codeTableService
								.getCTElementsUsingGET("CHANNEL", this.utils.getPreferredLanguage())
								.subscribe(value => {
									config.values = value;
								});
						}

						if (this.filterStatuses.channel?.value) {
							config.value = this.filterStatuses.channel.value as string;
						}
					}
				},
				date: {
					filter: {
						singleLevel: (values: Purchase[], value: [Date, Date]) => {
							const strDates = [value[0].toISOString(), new Date(value[1].getTime() + 864e5).toISOString()] as [string, string]; // if we choose day X as final date, we want to include purchases made on day X hence the day to be compared date must be higher
							return values.filter(v => v.datePurchase && v.datePurchase >= strDates[0] && v.datePurchase <= strDates[1]);
						}
					},
					reset: (config: FilterConfig<Purchase>) => { (this.filterStatuses.date ??= {} as FilterStatus).value = config.dateValues = config.dateRange },
					dateValues: [null, null],
					init: (config: FilterConfig<Purchase>) => {
						const singleEdges = ["3000", "1929-10-31"] as [string, string];
						for (const v of this.singleLevelItems as Purchase[]) {
							if (v.datePurchase) {
								if (singleEdges[0] > v.datePurchase) {
									singleEdges[0] = v.datePurchase;
								}
								if (singleEdges[1] < v.datePurchase) {
									singleEdges[1] = v.datePurchase;
								}
							}
						}
						if (singleEdges[0] === "3000" || singleEdges[0] === singleEdges[1]) { // if there are no dates or there is a single date for all the auctions, this field is hidden
							config.hideThis = true;
						} else {
							config.dateValues = singleEdges.map(s => new Date(s)) as [Date, Date]; // WARN: They must be separate objects
							config.dateRange = singleEdges.map(s => new Date(s)) as [Date, Date];

							/* If user previously selected values, they differed from previous edges.
							If they are equal, we keep them to the newly calculated edges.
							If they aren't
								1 - if they are still within the new range, we reset them to the previous value (e.g. I selected 01/01/2020 to 31/12/2020 and now the range is 01/01/2019 - 31/12/2021, I keep 01/01/2020 to 31/12/2020)
								2 - if not, previous range is cropped to the new limits (e.g. I selected 01/01/2020 to 31/12/2020 in a 01/01/2019 - 31/12/2021 range, but now the damage range is 01/06/2020 - 31/12/2021, so the new value is 01/06/2020 - 31/12/2029)
							*/
							if (this.filterStatuses.date) {
								if (this.filterStatuses.date.value) {
									const previousRange = this.filterStatuses.date.range as [Date, Date],
										previousValues = this.filterStatuses.date.value as [Date, Date];
									if (previousValues[0].getTime() !== previousRange[0].getTime() && previousValues[0] > config.dateRange[0] && previousValues[0] < config.dateRange[1]) {
										config.dateValues[0] = previousValues[0];
									}
									else {
										previousValues[0] = config.dateRange[0];
									}
									if (previousValues[1].getTime() !== previousRange[1].getTime() && previousValues[1] > config.dateRange[0] && previousValues[1] < config.dateRange[1]) {
										config.dateValues[1] = previousValues[1];
									}
									else {
										previousValues[1] = config.dateRange[1];
									}
									previousRange[0] = config.dateRange[0];
									previousRange[1] = config.dateRange[1];
								}
							}
							else {
								this.filterStatuses.date = { value: config.dateValues, range: config.dateRange };
							}
						}
					}
				}
			} as FilterConfigurationSet<Purchase>,
		};
	})();

	protected filterConfiguration: FilterConfigurationSet<VehicleDTO> | FilterConfigurationSet<Purchase>;
	private filterStatuses: { [key: string]: FilterStatus } = {};

	private filterSubject = new Subject<void>;
	private user: UserDTO;
	protected vehicleMarket: string;

	private initFilters() {
		for (const fieldKey of Object.keys(this.filterConfiguration)) {
			const config = this.filterConfiguration[fieldKey];
			if (config.init) {
				config.init(config);
			}
		}
	}

	resetFilters() {
		for (const fieldKey of Object.keys(this.filterConfiguration)) {
			const config = this.filterConfiguration[fieldKey];
			config.reset(config);
		}

		this.singleLevelChange.emit({ data: this.singleLevelItems, filtersHaveChanged: true });
		this.multiLevelChange.emit({ data: this.multiLevelItems, filtersHaveChanged: true });
	}

	ngOnDestroy() {
		if (this.filterStatuses.date) { // converting Dates to dateStrings
			this.filterStatuses.date.value = (this.filterStatuses.date.value as [Date, Date]).map(d => d.toISOString()) as [string, string];
			this.filterStatuses.date.range = (this.filterStatuses.date.range as [Date, Date]).map(d => d.toISOString()) as [string, string];
		}
		const sessionFilters = (this.storageService.getSessionItem(SessionKey.FILTERS_CONFIG, {}) as SessionSavedStatus);
		sessionFilters[this.configuration === "auctionDetails" ? this.idAuction.toString() : "purchase"] = this.filterStatuses;
		this.storageService.setSessionItem(SessionKey.FILTERS_CONFIG, sessionFilters);
	}

	ngOnInit() {
		const switchItOn = () => {
			/* NOTE: questa riga va prima di quella successiva, perché negli input c'è un controllo sincrono
			che impedisce di triggerare il subject se non c'è filterConfiguration.
			Diversamente, prima farebbe l'apply "sincrono", dopo l'init, e dopo l'auditTime ne farebbe un altro.
			*/
			this.refreshSubject
				.pipe(auditTime(500))
				.subscribe(this.refreshFilters);

			/* NOTE: questa riga va dopo quella precedente, perché negli input c'è un controllo sincrono
			che impedisce di triggerare il subject se non c'è filterConfiguration.
			Diversamente, prima farebbe l'apply "sincrono", dopo l'init, e dopo l'auditTime ne farebbe un altro.
			*/
			this.filterConfiguration = this.allConfigurations[this.configuration];
			// console.info("filterConfiguration"); console.table(this.filterConfiguration);
			this.filterStatuses = (this.storageService.getSessionItem(SessionKey.FILTERS_CONFIG, {}) as SessionSavedStatus)[this.configuration === "auctionDetails" ? this.idAuction.toString() : "purchase"] ?? {};

			// console.info("filterStatuses"); console.table(this.filterStatuses);
			if (this.filterStatuses.date) { // converting dateStrings to Date
				this.filterStatuses.date.value = (this.filterStatuses.date.value as [string, string]).map(s => new Date(s)) as [Date, Date];
				this.filterStatuses.date.range = (this.filterStatuses.date.range as [string, string]).map(s => new Date(s)) as [Date, Date];
			}

			this.initFilters();
			// console.info("apply init")
			this.applyFilters(/* true */);

			this.filterSubject
				.pipe(debounceTime(200))
				.subscribe(() => { this.applyFilters(/* true */); });
		};
		if (this.configuration) {
			switch (this.configuration) {
				case "auctionDetails":
					this.vehicleMarket = (this.singleLevelItems[0] as VehicleDTO || this.multiLevelItems[0].vehiclesDTO[0])?.idMarket ?? "IT";
				/* Falls through */
				case "purchase":
					this.userService.getProfileOfLoggedUser()
						.pipe(take(1))
						.subscribe(value => {
							this.user = value;
							switchItOn();
						});
			}
		}
	}

	/**
	 * Applies filters to the provided data, and emits the corresponding filtered data
	 * @param filtersHaveChanged - tells whether the filters have been applied since the filter values have changed or because the input data has been changed
	 */
	private applyFilters = (filtersHaveChanged = true) => {
		// console.info("dentro apply")
		let filteredSingles = this.singleLevelItems.slice(0),
			filteredMultis = this.multiLevelItems.slice(0);
		for (const fieldKey of Object.keys(this.filterConfiguration)) {
			const field = this.filterConfiguration[fieldKey];
			if (!field.hideThis) {
				// if (!((field.sliderConfig && field.rangeValue) && field.rangeValue[0] === field.sliderConfig.floor && field.rangeValue[1] === field.sliderConfig.ceil)) { // if the sliderconfig is set to the edges, there is no need to filter -> moved to single filter methods for granularity
				if (filteredSingles.length) {
					filteredSingles = field.filter.singleLevel(filteredSingles as VehicleDTO[], field.value ?? field.rangeValue ?? field.dateValues); // NOTE: cast to VehicleDTO only not to be annoyed by TS errors
				}
				if (filteredMultis.length && field.filter.multiLevel) {
					filteredMultis = field.filter.multiLevel(filteredMultis, field.value ?? field.rangeValue);
				}
				// }
			}
		}
		this.singleLevelChange.emit({ data: filteredSingles, filtersHaveChanged });
		this.multiLevelChange.emit({ data: filteredMultis, filtersHaveChanged });
	}

	protected dateFilterChanged(filter: string, value: Date, which: "from" | "to") {
		this.filterConfiguration[filter].dateValues[which === "from" ? 0 : 1] = value;
		this.filterSubject.next();
		((this.filterStatuses[filter] ??= {} as FilterStatus).value as [Date, Date])[which === "from" ? 0 : 1] = value;
	}

	protected rangeFilterChanged(filter: string, value: [number, number]) {
		this.filterConfiguration[filter].rangeValue = value;
		this.filterSubject.next();
		(this.filterStatuses[filter] ??= {} as FilterStatus).value = value;
	}

	protected filterChanged(filter: string, e: Event) {
		this.filterConfiguration[filter].value = (e.target as HTMLInputElement).value;
		this.filterSubject.next();
		(this.filterStatuses[filter] ??= {} as FilterStatus).value = (e.target as HTMLInputElement).value;
	}
}